/**
 * DealImportDlqMonitor - Tela admin (root) para inspeção da DLQ de
 * importação de negócios.
 *
 * Três abas:
 *   1. Lotes em DLQ (banco)    - deal_import_batches.status = 'failed'
 *   2. Jobs falhos (banco)     - deal_import_jobs.status in (failed, completed_with_errors)
 *   3. DLQ PGMQ (fila real)    - pgmq.q_dlq_deals_import com Reprocessar/Descartar
 *
 * CHANGELOG:
 *   RELEASE: 2026-05-16 v1.1.0 - Aba "DLQ PGMQ" com botões Reprocessar/Descartar
 *            (chama list_deal_import_dlq_messages / requeue_deal_import_dlq_message
 *            / discard_deal_import_dlq_message — todas root-only).
 *   RELEASE: 2026-05-16 v1.0.0 - Versão inicial somente-leitura.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Skull, Clock, AlertTriangle, RotateCw, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const JOB_STATUS_COLORS: Record<string, string> = {
  failed: 'bg-destructive/10 text-destructive',
  completed_with_errors: 'bg-amber-500/10 text-amber-600',
  cancelled: 'bg-muted text-muted-foreground',
};

const JOB_STATUS_LABELS: Record<string, string> = {
  failed: 'Falhou',
  completed_with_errors: 'Concluído com erros',
  cancelled: 'Cancelado',
};

export default function DealImportDlqMonitor() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pendingMsg, setPendingMsg] = useState<number | null>(null);

  // ===== Jobs com falha =====
  const jobsQuery = useQuery({
    queryKey: ['admin-deal-import-failed-jobs'],
    refetchInterval: 15000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('deal_import_jobs')
        .select('id, tenant_id, name, status, total_rows, succeeded_items, failed_items, skipped_items, created_at')
        .in('status', ['failed', 'completed_with_errors'])
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as any[];
    },
  });

  // ===== Batches em failed =====
  const batchesQuery = useQuery({
    queryKey: ['admin-deal-import-failed-batches'],
    refetchInterval: 15000,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('deal_import_batches')
        .select('id, job_id, batch_index, status, retry_count, last_error, succeeded_count, failed_count, updated_at')
        .eq('status', 'failed')
        .order('updated_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      return data as any[];
    },
  });

  // ===== Mensagens DLQ PGMQ reais =====
  const dlqQuery = useQuery({
    queryKey: ['admin-deal-import-dlq-pgmq'],
    refetchInterval: 15000,
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('list_deal_import_dlq_messages', { p_limit: 200 });
      if (error) throw error;
      return (data ?? []) as Array<{
        msg_id: number;
        enqueued_at: string;
        read_ct: number;
        message: any;
      }>;
    },
  });

  async function handleRefresh() {
    setIsRefreshing(true);
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['admin-deal-import-failed-jobs'] }),
      qc.invalidateQueries({ queryKey: ['admin-deal-import-failed-batches'] }),
      qc.invalidateQueries({ queryKey: ['admin-deal-import-dlq-pgmq'] }),
    ]);
    setLastUpdate(new Date());
    setIsRefreshing(false);
  }

  async function handleRequeueDlq(msgId: number) {
    setPendingMsg(msgId);
    try {
      const { data, error } = await (supabase as any).rpc('requeue_deal_import_dlq_message', { p_dlq_msg_id: msgId });
      if (error) throw error;
      toast({ title: 'Mensagem reenfileirada', description: `Nova msg_id: ${data}` });
      qc.invalidateQueries({ queryKey: ['admin-deal-import-dlq-pgmq'] });
    } catch (e: any) {
      toast({ title: 'Erro ao reprocessar', description: e.message, variant: 'destructive' });
    } finally {
      setPendingMsg(null);
    }
  }

  async function handleDiscardDlq(msgId: number) {
    if (!confirm(`Descartar mensagem ${msgId}? Esta ação é irreversível.`)) return;
    setPendingMsg(msgId);
    try {
      const { error } = await (supabase as any).rpc('discard_deal_import_dlq_message', { p_dlq_msg_id: msgId });
      if (error) throw error;
      toast({ title: 'Mensagem descartada' });
      qc.invalidateQueries({ queryKey: ['admin-deal-import-dlq-pgmq'] });
    } catch (e: any) {
      toast({ title: 'Erro ao descartar', description: e.message, variant: 'destructive' });
    } finally {
      setPendingMsg(null);
    }
  }

  function timeSince() {
    const s = Math.floor((Date.now() - lastUpdate.getTime()) / 1000);
    if (s < 60) return `há ${s}s`;
    return `há ${Math.floor(s / 60)}min`;
  }

  const failedJobsCount = jobsQuery.data?.length ?? 0;
  const failedBatchesCount = batchesQuery.data?.length ?? 0;
  const dlqMsgCount = dlqQuery.data?.length ?? 0;

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Skull className="h-8 w-8 text-destructive" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">DLQ — Importação de Negócios</h1>
            <p className="text-muted-foreground">
              Monitoramento e ações em jobs/lotes/mensagens em falha (cross-tenant, somente root).
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Atualizado {timeSince()}</span>
          </div>
          <Button onClick={handleRefresh} disabled={isRefreshing} variant="outline">
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {/* Status cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Jobs com falha</CardTitle>
            <AlertTriangle className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{failedJobsCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Inclui `failed` e `completed_with_errors`.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Lotes com falha</CardTitle>
            <Skull className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{failedBatchesCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Lotes que esgotaram tentativas.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Mensagens em DLQ PGMQ</CardTitle>
            <Skull className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{dlqMsgCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Mensagens reais em `dlq_deals_import` — passíveis de reprocesso.
            </p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="dlq">
        <TabsList>
          <TabsTrigger value="dlq">DLQ PGMQ ({dlqMsgCount})</TabsTrigger>
          <TabsTrigger value="batches">Lotes ({failedBatchesCount})</TabsTrigger>
          <TabsTrigger value="jobs">Jobs ({failedJobsCount})</TabsTrigger>
        </TabsList>

        {/* ===== DLQ PGMQ ===== */}
        <TabsContent value="dlq">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>msg_id</TableHead>
                  <TableHead>Batch ID</TableHead>
                  <TableHead>Tenant</TableHead>
                  <TableHead className="text-right">Tentativas</TableHead>
                  <TableHead>Último erro</TableHead>
                  <TableHead>Enfileirado</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dlqQuery.isLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell></TableRow>
                )}
                {!dlqQuery.isLoading && dlqMsgCount === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhuma mensagem em DLQ — tudo limpo!
                  </TableCell></TableRow>
                )}
                {dlqQuery.data?.map((m) => (
                  <TableRow key={m.msg_id}>
                    <TableCell className="font-mono text-xs">{m.msg_id}</TableCell>
                    <TableCell className="font-mono text-xs uppercase">
                      {String(m.message?.batch_id ?? '').slice(0, 8)}
                    </TableCell>
                    <TableCell className="font-mono text-xs uppercase">
                      {String(m.message?.tenant_id ?? '').slice(0, 8)}
                    </TableCell>
                    <TableCell className="text-right">{m.read_ct}</TableCell>
                    <TableCell className="text-xs max-w-md truncate" title={m.message?.final_error || ''}>
                      {m.message?.final_error || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {m.enqueued_at && format(new Date(m.enqueued_at), 'dd/MM HH:mm', { locale: ptBR })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="xs"
                          variant="outline"
                          disabled={pendingMsg === m.msg_id}
                          onClick={() => handleRequeueDlq(m.msg_id)}
                          title="Reenviar para deals_import"
                        >
                          <RotateCw className="h-3 w-3 mr-1" />
                          Reprocessar
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          disabled={pendingMsg === m.msg_id}
                          onClick={() => handleDiscardDlq(m.msg_id)}
                          title="Descartar permanentemente"
                        >
                          <Trash2 className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== Lotes ===== */}
        <TabsContent value="batches">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Job</TableHead>
                  <TableHead>Batch #</TableHead>
                  <TableHead className="text-right">Tentativas</TableHead>
                  <TableHead className="text-right">Sucesso</TableHead>
                  <TableHead className="text-right">Falhas</TableHead>
                  <TableHead>Último erro</TableHead>
                  <TableHead>Atualizado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batchesQuery.isLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell></TableRow>
                )}
                {batchesQuery.data?.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhum lote em falha.
                  </TableCell></TableRow>
                )}
                {batchesQuery.data?.map((b) => (
                  <TableRow key={b.id}>
                    <TableCell className="font-mono text-xs uppercase">{b.job_id?.slice(0, 8)}</TableCell>
                    <TableCell>{b.batch_index}</TableCell>
                    <TableCell className="text-right">{b.retry_count ?? 0}</TableCell>
                    <TableCell className="text-right text-green-600">{b.succeeded_count ?? 0}</TableCell>
                    <TableCell className="text-right text-destructive">{b.failed_count ?? 0}</TableCell>
                    <TableCell className="text-xs max-w-md truncate" title={b.last_error || ''}>
                      {b.last_error || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {b.updated_at && format(new Date(b.updated_at), 'dd/MM HH:mm', { locale: ptBR })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ===== Jobs ===== */}
        <TabsContent value="jobs">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Falhas</TableHead>
                  <TableHead className="text-right">Pulados</TableHead>
                  <TableHead>Criado em</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobsQuery.isLoading && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Carregando...
                  </TableCell></TableRow>
                )}
                {jobsQuery.data?.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Nenhum job em falha.
                  </TableCell></TableRow>
                )}
                {jobsQuery.data?.map((j) => (
                  <TableRow key={j.id}>
                    <TableCell className="font-mono text-xs uppercase">{j.tenant_id?.slice(0, 8)}</TableCell>
                    <TableCell className="font-medium">{j.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={JOB_STATUS_COLORS[j.status] || ''}>
                        {JOB_STATUS_LABELS[j.status] || j.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{j.total_rows?.toLocaleString('pt-BR') ?? 0}</TableCell>
                    <TableCell className="text-right text-destructive">{j.failed_items?.toLocaleString('pt-BR') ?? 0}</TableCell>
                    <TableCell className="text-right text-amber-600">{j.skipped_items?.toLocaleString('pt-BR') ?? 0}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {j.created_at && format(new Date(j.created_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
